import { NotificationStatus, PushHistorySource, StaffPushHistoryItem } from '@mygate/common';

export interface PushStatusDescription {
  tone: 'green' | 'red' | 'amber' | 'blue';
  label: string;
  hint: string;
}

// What each status proves, and no more: `SENT` also covers a send that reached only some devices (MYG-823 records
// each device's verdict, the status stays whole-send), and even `DELIVERED` ends at Apple or Google, not on the screen.
const STATUS_DESCRIPTIONS: Record<NotificationStatus, PushStatusDescription> = {
  [NotificationStatus.PENDING]: { tone: 'amber', label: 'Queued', hint: 'Not handed to the push service yet.' },
  [NotificationStatus.SENT]: {
    tone: 'blue',
    label: 'Sent',
    hint: 'Accepted by Expo. Apple or Google has not confirmed it, or only for some devices.',
  },
  [NotificationStatus.DELIVERED]: {
    tone: 'green',
    label: 'Delivered',
    hint: 'Accepted by Apple or Google. Whether the device displayed it is not tracked.',
  },
  [NotificationStatus.FAILED]: { tone: 'red', label: 'Failed', hint: 'Sending failed.' },
};

const formatCount = (count: number): string => count.toLocaleString('en-US');

export function describePushStatus(
  item: Pick<StaffPushHistoryItem, 'status' | 'errorMessage' | 'recipientCount'>,
): PushStatusDescription {
  const description = STATUS_DESCRIPTIONS[item.status];
  const hint = item.status === NotificationStatus.FAILED && item.errorMessage ? item.errorMessage : description.hint;
  if (item.recipientCount <= 1) return { ...description, hint };
  return {
    ...description,
    hint: `${hint} This is the status of the whole send to ${formatCount(item.recipientCount)} recipients, not of this customer alone.`,
  };
}

export function describePushOrigin(item: Pick<StaffPushHistoryItem, 'source' | 'sentBy' | 'recipientCount'>): string {
  switch (item.source) {
    case PushHistorySource.MANUAL:
      return item.sentBy?.name ? `Sent by ${item.sentBy.name}` : 'Sent by a staff account that no longer exists';
    case PushHistorySource.BROADCAST:
      return `Broadcast · ${formatCount(item.recipientCount)} recipient${item.recipientCount === 1 ? '' : 's'}`;
    case PushHistorySource.SYSTEM:
      return 'Automatic';
  }
}

/** The last page that still has pushes on it; an empty history still has page 1. */
export function lastHistoryPage(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}
